import React from 'react';

import { checkCacheStatus } from '../utilities';

import type { UseVersionCheckOptions } from './hooks.types';

export const useVersionCheck = ({
  enabled,
  metaFileDirectory,
  currentVersion,
  checkInterval,
  onNewVersionFound,
  onStatusChange,
  debug = false,
}: UseVersionCheckOptions) => {
  const intervalIdReference = React.useRef<
    ReturnType<typeof setInterval> | undefined
  >(undefined);
  const debugIntervalReference = React.useRef<
    ReturnType<typeof setInterval> | undefined
  >(undefined);
  const isUpdatingReference = React.useRef(false);
  const nextCheckTimeReference = React.useRef<number>(0);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const runCheck = async () => {
      if (isUpdatingReference.current) {
        return false;
      }

      if (debug) {
        console.log('[CacheBuster] Проверка новой версии...');
      }

      const { shouldUpdate, metaVersion } = await checkCacheStatus(
        metaFileDirectory,
        currentVersion,
      );

      if (shouldUpdate) {
        isUpdatingReference.current = true;

        if (debug) {
          console.log(
            `[CacheBuster] 🎉 Найдена новая версия: ${metaVersion} (текущая: ${currentVersion})`,
          );
        }

        onNewVersionFound();

        /*
         * Останавливаем интервалы. Гард `if (intervalIdReference.current)`
         * снят как мёртвый (AF-351): `runCheck` асинхронна и до этой строки
         * успевает дойти только после `await checkCacheStatus`, а
         * `setInterval` ниже по эффекту выполняется синхронно — то есть ссылка
         * к этому моменту уже проставлена всегда. Поведение не меняется и в
         * гипотетическом случае: `clearInterval(undefined)` ничего не делает.
         */
        clearInterval(intervalIdReference.current);
        intervalIdReference.current = undefined;
        if (debugIntervalReference.current) {
          clearInterval(debugIntervalReference.current);
          debugIntervalReference.current = undefined;
        }
      } else {
        if (debug) {
          console.log(`[CacheBuster] ✓ Версия актуальна: ${currentVersion}`);
        }

        onStatusChange(true);

        // Устанавливаем время следующей проверки
        nextCheckTimeReference.current = Date.now() + checkInterval;
      }

      return shouldUpdate;
    };

    const handleIntervalTick = () => {
      void runCheck();
    };

    // Debug таймер
    if (debug) {
      console.log(
        `[CacheBuster] Инициализация. Интервал проверки: ${checkInterval / 1000}с`,
      );

      debugIntervalReference.current = setInterval(() => {
        const now = Date.now();
        const timeLeft = nextCheckTimeReference.current - now;

        if (timeLeft > 0) {
          const secondsLeft = Math.ceil(timeLeft / 1000);
          const minutes = Math.floor(secondsLeft / 60);
          const seconds = secondsLeft % 60;

          console.log(
            `[CacheBuster] ⏱️  До следующей проверки: ${minutes}м ${seconds}с`,
          );
        }
      }, 10_000); // Выводим каждые 10 секунд
    }

    // Первоначальная проверка
    void runCheck();

    // Периодическая проверка
    intervalIdReference.current = setInterval(
      handleIntervalTick,
      checkInterval,
    );

    return () => {
      if (intervalIdReference.current) {
        clearInterval(intervalIdReference.current);
        intervalIdReference.current = undefined;
      }
      if (debugIntervalReference.current) {
        clearInterval(debugIntervalReference.current);
        debugIntervalReference.current = undefined;
      }
    };
  }, [
    enabled,
    checkInterval,
    metaFileDirectory,
    currentVersion,
    onNewVersionFound,
    onStatusChange,
    debug,
  ]);
};
